import type { JSONSchema7 } from 'json-schema';
import { QuestionForm } from 'types';

type Values = Record<string, unknown>;

/**
 * Structurally identical to informant's own `PersistenceConnector`, which it
 * does not re-export from `@updater/ui-informant`. Declared here so the wrapper
 * stays assignable to what `FormProvider` accepts.
 */
export interface PersistenceConnector {
  getOrCreateEntry(
    schema: JSONSchema7,
    initialValues?: unknown
  ): Promise<{ id: string; data: Values | null }>;
  save(entryId: string, values: Values): Promise<void>;
}

/**
 * Keeps seeded answers authoritative across a persisted session.
 *
 * Informant hands `initialValues` to the connector only when it creates an
 * entry; on every later visit it returns the stored data verbatim and discards
 * them. That is the right default for answers a visitor typed, but not for
 * answers a microsite supplies on their behalf — those describe the microsite,
 * not the visitor, and there is no longer a screen on which to correct them.
 *
 * Two failure modes this closes, both for a visitor whose stored entry predates
 * the microsite being configured to skip a question:
 *  - the stored entry never answered it, leaving a form that cannot be
 *    completed and no screen on which to complete it
 *  - the stored entry answered it differently, resurrecting the screens that
 *    answer was meant to skip
 *
 * With an empty seed this is a pass-through.
 */
export function withSeededAnswers(
  connector: PersistenceConnector,
  seed: Partial<QuestionForm>
): PersistenceConnector {
  return {
    save: connector.save,
    getOrCreateEntry: async (schema, initialValues) => {
      const entry = await connector.getOrCreateEntry(schema, initialValues);

      return { ...entry, data: { ...entry.data, ...seed } };
    },
  };
}
